import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { MedicationQuestionStep } from '@features/onboarding/components/6-health-question-form/components/medication-question/MedicationQuestionStep';
import { useHealthStore } from 'src/store/health';

interface HealthQuestionsStepProps {
  form: UseFormReturn<OnboardingSchema| any>;
  formData: Partial<OnboardingSchema>;
  updateFormData: (data: Partial<OnboardingSchema>) => void;
}

export const HealthQuestionsStep: React.FC<HealthQuestionsStepProps> = ({ form }) => {
  const isTakingMedications = form.watch('healthQuestions.medicationQuestion.medications') === 'true';
  const isUnderTreatment = form.watch('healthQuestions.initialQuestions.treatment') === 'true';
  const { issues, issueDetails, conditions } = useHealthStore();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Health Questionnaire
        </h2>
        <p className="text-gray-600 mb-6">
          We need to ask you a few quick health questions to get to know you better. This helps us determine your eligibility and provide accurate pricing.
        </p>
      </div>

      {/* Initial Questions */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Initial Health Questions</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Are you currently under treatment for any medical condition?
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  {...form.register('healthQuestions.initialQuestions.treatment')}
                  value="true"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  {...form.register('healthQuestions.initialQuestions.treatment')}
                  value="false"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>

          {/* Medical Conditions - показывается только если пользователь под лечением */}
          {isUnderTreatment && (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Please specify which medical conditions you are being treated for:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {conditions.sort((a: any, b: any) => a.label.localeCompare(b.label)).map((detail) => (
                  <label key={detail.key} className="flex items-center">
                    <input
                      type="checkbox"
                      value={detail.key}
                      onChange={(e) => {
                        const currentConditions = form.watch('healthQuestions.surgeriesDiabetesHeartMental.conditions') || [];
                        if (e.target.checked) {
                          form.setValue('healthQuestions.surgeriesDiabetesHeartMental.conditions', [
                            ...currentConditions,
                            { value: detail.key }
                          ]);
                        } else {
                          form.setValue('healthQuestions.surgeriesDiabetesHeartMental.conditions', 
                            currentConditions.filter((c: any) => c.value !== detail.key)
                          );
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{detail.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Are you currently pregnant?
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  {...form.register('healthQuestions.initialQuestions.pregnancy')}
                  value="true"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  {...form.register('healthQuestions.initialQuestions.pregnancy')}
                  value="false"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Do you have any disabilities or are you receiving disability benefits?
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  {...form.register('healthQuestions.initialQuestions.invalid')}
                  value="true"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  {...form.register('healthQuestions.initialQuestions.invalid')}
                  value="false"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Medications */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Medications</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Are you currently taking any medications?
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                {...form.register('healthQuestions.medicationQuestion.medications')}
                value="true"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                {...form.register('healthQuestions.medicationQuestion.medications')}
                value="false"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">No</span>
            </label>
          </div>
        </div>
    
        {/* Medication Component - показывается только если пользователь принимает лекарства */}
        {isTakingMedications && (
          <div className="mt-6">
            <MedicationQuestionStep question={{id: 'takingMedicationsNow', question:'question', type: 'takingMedicationsNow'}} form={form} />
          </div>
        )}
      </div>

      {/* Health Conditions */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Health Conditions</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Do you have any of the following conditions? (Select all that apply)
          </label>
          <div className="space-y-2">
            {issues.map((issue) => (
              <label key={issue.key} className="flex items-center">
                <input
                  type="checkbox"
                  value={issue.key}
                  onChange={(e) => {
                    const currentConditions = form.watch('healthQuestions.surgeriesDiabetesHeartMental.conditions') || [];
                    if (e.target.checked) {
                      form.setValue('healthQuestions.surgeriesDiabetesHeartMental.conditions', [
                        ...currentConditions,
                        { value: issue.key }
                      ]);
                    } else {
                      form.setValue('healthQuestions.surgeriesDiabetesHeartMental.conditions', 
                        currentConditions.filter((c: any) => c.value !== issue.key)
                      );
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{issue.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Condition Details */}
      {(() => {
        const selectedConditions = form.watch('healthQuestions.surgeriesDiabetesHeartMental.conditions') || [];
        if (!selectedConditions.length) return null;

        // Группируем выбранные условия по issueId
        const groupedConditions = selectedConditions.reduce((groups: any, condObj: any) => {
          const condKey = condObj.value;
          const issue = issues.find((i) => i.key === condKey);
          if (!issue) return groups;
          
          if (!groups[issue.id]) {
            groups[issue.id] = {
              issue,
              details: []
            };
          }
          
          const detailsForIssue = issueDetails.filter((d) => d.issueId === issue.id);
          groups[issue.id].details = detailsForIssue;
          
          return groups;
        }, {});

        return Object.values(groupedConditions).map((group: any) => {
          if (!group.details.length) return null;
          
          return (
            <div key={group.issue.id} className="mt-4 space-y-2 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="text-md font-medium text-gray-900">
                Additional details for {group.issue.label}:
              </h4>
              {group.details.map((detail: any) => {
                const detailPath = `healthQuestions.surgeriesDiabetesHeartMental.details.${detail.key}` as const;
                const checked = !!form.watch(detailPath);
                return (
                  <label key={detail.key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          form.setValue(detailPath, true);
                        } else {
                          form.setValue(detailPath, false);
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{detail.label}</span>
                  </label>
                );
              })}
            </div>
          );
        });
      })()}

      {/* Hidden Fields Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-blue-900 mb-2">Automatic Information</h3>
        <div className="space-y-2 text-sm text-blue-700">
          <div>
            <strong>Coverage Start Date:</strong> First day of next month (automatically set)
          </div>
          <div>
            <strong>Privacy Disclosure:</strong> Automatically accepted
          </div>
          <div>
            <strong>Electronic Signature:</strong> Automatically provided
          </div>
        </div>
      </div>
    </div>
  );
}; 